import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import cytoscape, { Core, EventObject, ElementDefinition, NodeSingular } from 'cytoscape';
import '../../../core/cytoscape-setup';
import { PolicyFlowGraph } from '../../../core/services/policy-flow';

type InspectKind = 'flow' | 'step' | 'policy';

interface Inspected {
  kind: InspectKind;
  title: string;
  meta: Array<{ label: string; value: string }>;
}

const COLORS: Record<InspectKind, string> = {
  flow: '#F59E0B',
  step: '#14B8A6',
  policy: '#8B5CF6',
};

@Component({
  selector: 'app-policy-graph',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './policy-graph.html',
  styleUrl: './policy-graph.css',
})
export class PolicyGraph implements AfterViewInit, OnDestroy {
  @Input() set graph(value: PolicyFlowGraph | null) {
    this._graph.set(value);
    if (this.viewReady) this.render();
  }

  @ViewChild('graphCanvas') graphCanvas?: ElementRef<HTMLDivElement>;

  private _graph = signal<PolicyFlowGraph | null>(null);
  private cy: Core | null = null;
  private viewReady = false;

  readonly colors = COLORS;

  show = signal<{ flow: boolean; step: boolean; policy: boolean }>({
    flow: true,
    step: true,
    policy: true,
  });

  /** Flow filter: null = whole graph, otherwise scope to one flow. */
  flowFilter = signal<string | null>(null);

  inspected = signal<Inspected | null>(null);
  hasFocus = signal(false);

  flowOptions = computed(() =>
    (this._graph()?.flows ?? []).map(f => ({ id: f.flow_id, name: f.name }))
  );

  orphanCount = computed(() => {
    const g = this._graph();
    if (!g) return 0;
    const governed = new Set(g.governs.map(x => x.policy_id));
    return g.policies.filter(p => !governed.has(p.policy_id)).length;
  });

  constructor() {
    // Re-apply type visibility whenever a legend toggle changes.
    effect(() => {
      const s = this.show();
      if (!this.cy) return;
      (['flow', 'step', 'policy'] as InspectKind[]).forEach(t => {
        this.cy!.nodes(`[type = "${t}"]`).style('display', s[t] ? 'element' : 'none');
      });
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  ngOnDestroy(): void {
    this.cy?.destroy();
    this.cy = null;
  }

  toggle(kind: InspectKind) {
    this.show.update(s => ({ ...s, [kind]: !s[kind] }));
  }

  onFlowFilterChange(value: string) {
    this.flowFilter.set(value || null);
    this.render();
  }

  resetView() {
    this.clearFocus();
    this.inspected.set(null);
    this.cy?.animate({ fit: { eles: this.cy.elements(), padding: 40 } }, { duration: 300 });
  }

  // ---- cytoscape ----

  private ensureCy(): Core | null {
    const container = this.graphCanvas?.nativeElement;
    if (!container) return null;
    if (this.cy) return this.cy;

    this.cy = cytoscape({
      container,
      elements: [],
      style: [
        {
          selector: 'node',
          style: {
            label: 'data(label)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '10px',
            color: '#334155',
            'text-valign': 'bottom',
            'text-margin-y': 4,
            'text-wrap': 'wrap',
            'text-max-width': '110px',
            'text-opacity': 0, // hidden by default; shown for flows + focused nodes
            'border-width': 2,
            'border-color': '#FFFFFF',
            'transition-property': 'opacity, border-color, border-width',
            'transition-duration': 180 as any,
            width: 18,
            height: 18,
          },
        },
        {
          selector: 'node[type = "flow"]',
          style: {
            'background-color': COLORS.flow,
            width: 30,
            height: 30,
            color: '#0F172A',
            'font-size': '11px',
            'font-weight': 600 as any,
            'text-opacity': 1,
            'border-color': '#FDE68A',
          },
        },
        {
          selector: 'node[type = "step"]',
          style: { 'background-color': COLORS.step, shape: 'round-rectangle', 'border-color': '#99F6E4', width: 22, height: 22 },
        },
        {
          selector: 'node[type = "policy"]',
          style: { 'background-color': COLORS.policy, shape: 'diamond', 'border-color': '#DDD6FE' },
        },
        {
          selector: 'node.show-label',
          style: { 'text-opacity': 1 },
        },
        {
          selector: 'node.focus',
          style: {
            'border-width': 4,
            'border-color': '#0F172A',
            'text-opacity': 1,
            'font-weight': 600 as any,
            'z-index': 999 as any,
          },
        },
        {
          selector: 'edge',
          style: {
            width: 1.2,
            'curve-style': 'bezier',
            'line-opacity': 0.55,
            'target-arrow-shape': 'triangle',
            'arrow-scale': 0.7,
            'transition-property': 'opacity, line-opacity',
            'transition-duration': 180 as any,
          },
        },
        { selector: 'edge[rel = "contains"]', style: { 'line-color': '#CBD5E1', 'target-arrow-color': '#CBD5E1', 'line-style': 'dashed' } },
        { selector: 'edge[rel = "transition"]', style: { 'line-color': '#14B8A6', 'target-arrow-color': '#14B8A6', width: 1.8, 'line-opacity': 0.85 } },
        { selector: 'edge[rel = "governs"]', style: { 'line-color': '#C4B5FD', 'target-arrow-color': '#C4B5FD', 'line-opacity': 0.4 } },
        // Focus mode: everything not in the selected neighborhood fades back.
        { selector: 'node.faded', style: { opacity: 0.1, 'text-opacity': 0 } },
        { selector: 'edge.faded', style: { opacity: 0.05 } },
        { selector: 'edge.hot', style: { 'line-opacity': 0.95, width: 2.2 } },
      ],
      layout: { name: 'preset' },
      minZoom: 0.15,
      maxZoom: 3,
      wheelSensitivity: 0.2,
    });

    this.cy.on('tap', 'node', (ev: EventObject) => this.onNodeTap(ev.target as NodeSingular));
    this.cy.on('tap', (ev: EventObject) => {
      if (ev.target === this.cy) {
        this.clearFocus();
        this.inspected.set(null);
      }
    });

    return this.cy;
  }

  /** Builds the element set for the current flow filter (whole graph, or one flow). */
  private buildElements(g: PolicyFlowGraph): ElementDefinition[] {
    const filter = this.flowFilter();
    const flows = filter ? g.flows.filter(f => f.flow_id === filter) : g.flows;

    const els: ElementDefinition[] = [];
    const stepIds = new Set<string>();

    flows.forEach(f => {
      els.push({ data: { id: `f:${f.flow_id}`, label: f.name, type: 'flow' } });
      f.steps.forEach(s => {
        stepIds.add(s.step_id);
        els.push({ data: { id: `s:${s.step_id}`, label: s.name, type: 'step' } });
        els.push({ data: { id: `c:${f.flow_id}:${s.step_id}`, source: `f:${f.flow_id}`, target: `s:${s.step_id}`, rel: 'contains' } });
      });
      f.transitions.forEach((t, i) => {
        if (stepIds.has(t.from_step_id) && stepIds.has(t.to_step_id)) {
          els.push({ data: { id: `t:${f.flow_id}:${i}`, source: `s:${t.from_step_id}`, target: `s:${t.to_step_id}`, rel: 'transition' } });
        }
      });
    });

    // Policies: all when unfiltered; only those governing the in-scope steps when filtered.
    const policyInScope = new Set<string>();
    g.governs.forEach(gov => {
      if (stepIds.has(gov.step_id)) policyInScope.add(gov.policy_id);
    });
    const policies = filter ? g.policies.filter(p => policyInScope.has(p.policy_id)) : g.policies;
    policies.forEach(p => {
      els.push({ data: { id: `p:${p.policy_id}`, label: p.statement.slice(0, 44), type: 'policy' } });
    });

    g.governs.forEach((gov, i) => {
      if (stepIds.has(gov.step_id) && (!filter || policyInScope.has(gov.policy_id))) {
        els.push({ data: { id: `g:${i}`, source: `p:${gov.policy_id}`, target: `s:${gov.step_id}`, rel: 'governs' } });
      }
    });

    return els;
  }

  private render() {
    const cy = this.ensureCy();
    const g = this._graph();
    if (!cy) return;

    cy.elements().remove();
    this.clearFocus();
    this.inspected.set(null);
    if (!g) return;

    cy.add(this.buildElements(g));
    cy.layout({
      name: 'fcose',
      animate: false,
      randomize: true,
      nodeRepulsion: 7000,
      idealEdgeLength: (edge: any) => (edge.data('rel') === 'governs' ? 90 : 60),
      nodeSeparation: 90,
    } as any).run();

    // Re-apply legend visibility to the fresh elements.
    const s = this.show();
    (['flow', 'step', 'policy'] as InspectKind[]).forEach(t => {
      cy.nodes(`[type = "${t}"]`).style('display', s[t] ? 'element' : 'none');
    });
  }

  /** Focus a node: keep it + its neighbourhood sharp, fade the rest. */
  private onNodeTap(node: NodeSingular) {
    if (!this.cy) return;
    this.setInspected(node.id());

    const neighborhood = node.closedNeighborhood();
    this.cy.elements().addClass('faded');
    neighborhood.removeClass('faded');
    neighborhood.nodes().addClass('show-label');
    node.connectedEdges().removeClass('faded').addClass('hot');

    this.cy.nodes().removeClass('focus');
    node.addClass('focus');
    this.hasFocus.set(true);

    this.cy.animate({ fit: { eles: neighborhood, padding: 80 } }, { duration: 300 });
  }

  private clearFocus() {
    this.cy?.elements().removeClass('faded hot focus show-label');
    this.hasFocus.set(false);
  }

  private setInspected(id: string) {
    const g = this._graph();
    if (!g) return;
    const [prefix, ...rest] = id.split(':');
    const rawId = rest.join(':');

    if (prefix === 'f') {
      const f = g.flows.find(x => x.flow_id === rawId);
      if (f) {
        this.inspected.set({
          kind: 'flow',
          title: f.name,
          meta: [
            { label: 'Steps', value: String(f.steps.length) },
            { label: 'Transitions', value: String(f.transitions.length) },
            { label: 'Review', value: f.review ? 'approved' : 'pending' },
          ],
        });
      }
    } else if (prefix === 's') {
      const step = g.flows.flatMap(f => f.steps).find(s => s.step_id === rawId);
      const governingCount = g.governs.filter(x => x.step_id === rawId).length;
      if (step) {
        this.inspected.set({
          kind: 'step',
          title: step.name,
          meta: [
            { label: 'Description', value: step.description },
            { label: 'Governed by', value: `${governingCount} policies` },
          ],
        });
      }
    } else if (prefix === 'p') {
      const p = g.policies.find(x => x.policy_id === rawId);
      const governed = g.governs.some(x => x.policy_id === rawId);
      if (p) {
        this.inspected.set({
          kind: 'policy',
          title: p.statement,
          meta: [
            { label: 'Type', value: p.policy_type },
            { label: 'Review', value: p.review ? 'approved' : 'pending' },
            { label: 'Evidence', value: p.evidence_text },
            ...(governed ? [] : [{ label: 'Note', value: 'Orphan — governs no step' }]),
          ],
        });
      }
    }
  }
}
